import React, { useEffect, useState } from 'react';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { Nav, Footer, DisclaimerBar } from '../components/Chrome';
import { useI18n } from '../i18n';
import { api, authHeaders, setAdminToken, getAdminToken } from '../api';

// ============ LOGIN ============
function AdminLogin({ onLogin }) {
  const { t } = useI18n();
  const [token, setToken] = useState('');
  const [err, setErr] = useState('');
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/admin/verify', {}, { headers: { 'X-Admin-Token': token } });
      setAdminToken(token);
      onLogin();
    } catch (_) {
      setErr(t('admin.login.error'));
    }
  };
  return (
    <div className="max-w-md mx-auto py-24 px-6">
      <div className="mono text-[10px] uppercase tracking-widest2 text-gold">ADMIN</div>
      <h1 className="serif text-5xl mt-4">{t('admin.login.title')}</h1>
      <p className="mt-3 text-white/60">{t('admin.login.sub')}</p>
      <form onSubmit={submit} className="mt-8 space-y-3" data-testid="admin-login-form">
        <input
          value={token}
          onChange={e => setToken(e.target.value)}
          type="password"
          placeholder="Admin Token"
          className="w-full bg-obsidian border hairline px-3 py-3 mono text-sm text-white placeholder-white/30 focus:border-gold outline-none"
          data-testid="admin-token-input"
        />
        {err && <div className="text-red-400 mono text-xs">{err}</div>}
        <button type="submit" className="w-full py-3 bg-gold text-obsidian mono text-[11px] uppercase tracking-widest2 hover:bg-white" style={{transitionProperty: 'background-color'}} data-testid="admin-login-submit">
          {t('admin.login.button')} →
        </button>
      </form>
    </div>
  );
}

// ============ LAYOUT ============
function AdminShell({ children, onLogout }) {
  const { t } = useI18n();
  const loc = useLocation();
  const links = [
    ['/admin', t('admin.nav.overview')],
    ['/admin/intakes', t('admin.nav.intakes')],
    ['/admin/generate', t('admin.nav.generate')],
    ['/admin/reports', t('admin.nav.reports')],
    ['/admin/radar', t('admin.nav.radar')],
    ['/admin/pilot', t('admin.nav.pilot')],
    ['/admin/leads', t('admin.nav.leads')],
  ];
  return (
    <div className="grid grid-cols-12 min-h-screen">
      <aside className="col-span-12 md:col-span-2 border-r hairline bg-surface1 p-4 md:p-6">
        <div className="mono text-[10px] uppercase tracking-widest2 text-gold mb-6">CONSOLE</div>
        <nav className="space-y-1">
          {links.map(([to, label]) => {
            const active = loc.pathname === to;
            return (
              <Link key={to} to={to} className={`block px-3 py-2 mono text-[11px] uppercase tracking-widest2 border-l-2 ${active ? 'border-gold text-gold bg-obsidian' : 'border-transparent text-white/60 hover:text-white'}`} data-testid={`admin-nav-${to.replace(/\//g,'-')}`}>
                {label}
              </Link>
            );
          })}
        </nav>
        <button onClick={onLogout} className="mt-6 mono text-[10px] uppercase tracking-widest2 text-white/40 hover:text-red-400" data-testid="admin-logout">
          ▸ {t('admin.logout')}
        </button>
      </aside>
      <main className="col-span-12 md:col-span-10 p-6 md:p-10">
        {children}
      </main>
    </div>
  );
}

// ============ OVERVIEW ============
function Overview() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get('/admin/stats', { headers: authHeaders() }).then(r => setStats(r.data));
  }, []);
  if (!stats) return <div className="mono text-xs text-white/40">Loading<span className="blink">_</span></div>;

  const Stat = ({ label, value, sub }) => (
    <div className="border hairline bg-surface1 p-6">
      <div className="mono text-[10px] uppercase tracking-widest2 text-white/40">{label}</div>
      <div className="serif text-5xl mt-4 text-white">{value}</div>
      {sub && <div className="mono text-[10px] uppercase tracking-widest2 text-gold mt-2">{sub}</div>}
    </div>
  );
  return (
    <div>
      <div className="mono text-[10px] uppercase tracking-widest2 text-gold">DECISION FACTORY</div>
      <h1 className="serif text-4xl mt-2">Overview</h1>
      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Leads" value={stats.leads} />
        <Stat label="Intakes" value={stats.intakes} />
        <Stat label="Reports" value={stats.reports} />
        <Stat label="Revenue (paid)" value={`$${Number(stats.revenue).toLocaleString()}`} sub={`${stats.paid_entries} paid`} />
        <Stat label="Pilot entries" value={stats.pilot_entries} />
        <Stat label="Referrals" value={stats.referrals} />
        <Stat label="Repeats" value={stats.repeats} />
        <Stat label="In-progress" value={stats.funnel.in_progress} sub={`${stats.funnel.new} new · ${stats.funnel.delivered} delivered`} />
      </div>
    </div>
  );
}

// ============ INTAKES ============
function Intakes() {
  const [items, setItems] = useState([]);
  const load = () => api.get('/admin/intakes', { headers: authHeaders() }).then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    await api.patch(`/admin/intakes/${id}`, { status }, { headers: authHeaders() });
    load();
  };

  return (
    <div>
      <h1 className="serif text-4xl">Intakes</h1>
      <div className="mt-8 border hairline bg-surface1 overflow-hidden">
        <div className="grid grid-cols-12 mono text-[10px] uppercase tracking-widest2 text-white/40 border-b hairline">
          <div className="col-span-2 p-3">Client</div>
          <div className="col-span-3 p-3">Property</div>
          <div className="col-span-2 p-3">Product</div>
          <div className="col-span-1 p-3 text-right">Asking</div>
          <div className="col-span-1 p-3">Status</div>
          <div className="col-span-3 p-3 text-right">Actions</div>
        </div>
        {items.map(it => (
          <div key={it.id} className="grid grid-cols-12 border-b hairline last:border-0 mono text-xs hover:bg-surface2/40" data-testid={`intake-row-${it.id}`}>
            <div className="col-span-2 p-3">
              <div className="text-white">{it.client_name}</div>
              <div className="text-white/40 text-[10px]">{it.client_email}</div>
            </div>
            <div className="col-span-3 p-3 text-white/80">{it.address}</div>
            <div className="col-span-2 p-3 text-white/60">{it.product}</div>
            <div className="col-span-1 p-3 text-right text-white/70">{it.asking_price ? `$${Number(it.asking_price).toLocaleString()}` : '—'}</div>
            <div className="col-span-1 p-3">
              <span className={`px-1.5 py-0.5 text-[10px] uppercase border ${it.status === 'new' ? 'text-gold border-gold/40' : it.status === 'in_progress' ? 'text-amber-400 border-amber-400/40' : it.status === 'delivered' ? 'text-emerald-400 border-emerald-400/40' : 'text-red-400 border-red-400/40'}`}>{it.status}</span>
            </div>
            <div className="col-span-3 p-3 flex justify-end gap-2">
              <button onClick={() => setStatus(it.id, 'in_progress')} className="mono text-[10px] uppercase tracking-widest2 border hairline px-2 py-1 hover:border-gold hover:text-gold" style={{transitionProperty: 'color, border-color'}} data-testid={`intake-progress-${it.id}`}>▶ Start</button>
              <Link to={`/admin/generate?intake=${it.id}`} className="mono text-[10px] uppercase tracking-widest2 border border-gold text-gold px-2 py-1 hover:bg-gold hover:text-obsidian" style={{transitionProperty: 'color, background-color'}} data-testid={`intake-generate-${it.id}`}>Generate</Link>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="p-8 text-center mono text-xs text-white/40">No intakes yet.</div>}
      </div>
    </div>
  );
}

// ============ GENERATE ============
function Generate() {
  const nav = useNavigate();
  const [intakes, setIntakes] = useState([]);
  const [selected, setSelected] = useState('');
  const [form, setForm] = useState({
    address: '', folio: '', zip_code: '', county: '', asking_price: '', intended_use: 'buy_hold',
    buyer_profile: '', timeline: '', known_risks: '', market_notes: '', lang: 'en',
  });
  const [status, setStatus] = useState('idle');
  const [err, setErr] = useState('');
  const [enrichFema, setEnrichFema] = useState(true);
  const [floodPreview, setFloodPreview] = useState(null);
  const [floodLoading, setFloodLoading] = useState(false);
  const [mlsFile, setMlsFile] = useState(null);
  const [mlsExtraction, setMlsExtraction] = useState(null);
  const [mlsUploading, setMlsUploading] = useState(false);
  const [mlsError, setMlsError] = useState('');
  // Pythia Atlas Decision Pack
  const [atlasPack, setAtlasPack] = useState(null);
  const [atlasLoading, setAtlasLoading] = useState(false);
  const [atlasError, setAtlasError] = useState('');
  const [atlasAutoDiscover, setAtlasAutoDiscover] = useState(false);

  useEffect(() => {
    api.get('/admin/intakes', { headers: authHeaders() }).then(r => setIntakes(r.data.filter(i => i.status !== 'delivered')));
    const p = new URLSearchParams(window.location.search).get('intake');
    if (p) setSelected(p);
  }, []);

  useEffect(() => {
    if (!selected) return;
    const it = intakes.find(i => i.id === selected);
    if (it) {
      setForm({
        address: it.address || '', folio: it.folio || '', zip_code: it.zip_code || '', county: it.county || '',
        asking_price: it.asking_price || '',
        intended_use: it.intended_use || 'buy_hold', buyer_profile: it.buyer_profile || '',
        timeline: it.timeline || '', known_risks: it.known_risks || '', market_notes: '', lang: it.lang || 'en',
      });
    }
  }, [selected, intakes]);

  const fetchAtlasPack = async () => {
    if (!form.address && !form.zip_code) {
      setAtlasError('Provide at least an address or ZIP.');
      return;
    }
    setAtlasLoading(true);
    setAtlasError('');
    setAtlasPack(null);
    try {
      const payload = {
        address: form.address || null,
        folio: form.folio || null,
        zip: form.zip_code || null,
        county: form.county || null,
        property_price: form.asking_price ? Number(form.asking_price) : null,
        options: { auto_discover: atlasAutoDiscover },
      };
      // strip nulls in top-level (leave options intact)
      const clean = Object.fromEntries(Object.entries(payload).filter(([k, v]) => k === 'options' || (v !== null && v !== '')));
      const r = await api.post('/indicators/decision-pack', clean, { headers: authHeaders() });
      setAtlasPack(r.data);
    } catch (e) {
      setAtlasError(e?.response?.data?.detail || 'Atlas decision-pack fetch failed');
    }
    setAtlasLoading(false);
  };

  const checkFlood = async () => {
    if (!form.address) return;
    setFloodLoading(true);
    setFloodPreview(null);
    try {
      const r = await api.get('/admin/enrich/flood', { headers: authHeaders(), params: { address: form.address } });
      setFloodPreview(r.data);
    } catch (e) {
      setFloodPreview({ error: e?.response?.data?.detail || 'lookup failed' });
    }
    setFloodLoading(false);
  };

  const uploadMls = async () => {
    if (!mlsFile) return;
    if (mlsFile.size > 15 * 1024 * 1024) {
      setMlsError('File exceeds 15MB limit.');
      return;
    }
    setMlsUploading(true);
    setMlsError('');
    setMlsExtraction(null);
    try {
      const fd = new FormData();
      fd.append('file', mlsFile);
      if (selected) fd.append('intake_id', selected);
      const r = await api.post('/admin/mls/extract', fd, {
        headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      setMlsExtraction(r.data);
    } catch (e) {
      setMlsError(e?.response?.data?.detail || 'MLS extraction failed');
    }
    setMlsUploading(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setStatus('sending'); setErr('');
    try {
      const payload = {
        ...form,
        asking_price: form.asking_price ? Number(form.asking_price) : null,
        intake_id: selected || null,
        enrich_fema: enrichFema,
        mls_extraction_id: mlsExtraction?.id || null,
        atlas_pack: atlasPack || null,
      };
      const res = await api.post('/admin/reports/generate', payload, { headers: authHeaders() });
      const jobId = res.data.job_id;
      let attempts = 0;
      const MAX_ATTEMPTS = 40;
      const poll = async () => {
        attempts += 1;
        if (attempts > MAX_ATTEMPTS) {
          setStatus('error');
          setErr('Generation timed out after 2 minutes. Please try again.');
          return;
        }
        try {
          const j = await api.get(`/admin/reports/jobs/${jobId}`, { headers: authHeaders() });
          if (j.data.status === 'done') {
            nav(`/report/${j.data.report_id}`);
          } else if (j.data.status === 'error') {
            setStatus('error');
            setErr(j.data.error || 'Generation failed');
          } else {
            setTimeout(poll, 3000);
          }
        } catch (pe) {
          setStatus('error');
          setErr('Polling error');
        }
      };
      setTimeout(poll, 3000);
    } catch (e) {
      setStatus('error');
      setErr(e?.response?.data?.detail || 'Generation failed');
    }
  };

  const upd = (k, v) => setForm({ ...form, [k]: v });
  const inputCls = "bg-obsidian border hairline px-3 py-2.5 mono text-sm text-white placeholder-white/30 focus:border-gold outline-none";
  const labelCls = "flex flex-col gap-2";
  const labelSpanCls = "mono text-[10px] uppercase tracking-widest2 text-white/50";

  return (
    <div>
      <h1 className="serif text-4xl">Generate Report</h1>
      <p className="mt-2 text-white/60">Claude Sonnet 4.5 · Pythia claim-safe framework · strict JSON output.</p>

      <form onSubmit={submit} className="mt-8 max-w-4xl space-y-4" data-testid="generate-form">
        <div className="border hairline bg-surface1 p-6">
          <label className={labelCls}>
            <span className={labelSpanCls}>Link to intake (optional)</span>
            <select value={selected} onChange={e => setSelected(e.target.value)} className={inputCls} data-testid="generate-intake-select">
              <option value="">— Manual / no intake —</option>
              {intakes.map(i => <option key={i.id} value={i.id}>{i.client_name} · {i.address}</option>)}
            </select>
          </label>
        </div>

        <div className="border hairline bg-surface1 p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className={labelCls}><span className={labelSpanCls}>Address</span><input required className={inputCls} value={form.address} onChange={e=>upd('address', e.target.value)} data-testid="generate-address" /></label>
          <label className={labelCls}><span className={labelSpanCls}>Folio</span><input className={inputCls} value={form.folio} onChange={e=>upd('folio', e.target.value)} /></label>
          <label className={labelCls}><span className={labelSpanCls}>ZIP code</span><input className={inputCls} value={form.zip_code} onChange={e=>upd('zip_code', e.target.value)} data-testid="generate-zip" placeholder="e.g. 33139" /></label>
          <label className={labelCls}><span className={labelSpanCls}>County (optional)</span><input className={inputCls} value={form.county} onChange={e=>upd('county', e.target.value)} data-testid="generate-county" placeholder="e.g. Miami-Dade" /></label>
          <label className={labelCls}><span className={labelSpanCls}>Asking price</span><input type="number" className={inputCls} value={form.asking_price} onChange={e=>upd('asking_price', e.target.value)} data-testid="generate-asking" /></label>
          <label className={labelCls}><span className={labelSpanCls}>Intended use</span>
            <select className={inputCls} value={form.intended_use} onChange={e=>upd('intended_use', e.target.value)}>
              {['buy_hold','fix_flip','primary','wholesale','listing_autopsy','other'].map(u => <option key={u} value={u}>{u.replace('_', ' ')}</option>)}
            </select>
          </label>
          <label className={labelCls}><span className={labelSpanCls}>Buyer profile</span><input className={inputCls} value={form.buyer_profile} onChange={e=>upd('buyer_profile', e.target.value)} /></label>
          <label className={labelCls}><span className={labelSpanCls}>Timeline</span><input className={inputCls} value={form.timeline} onChange={e=>upd('timeline', e.target.value)} /></label>
          <label className={`md:col-span-2 ${labelCls}`}><span className={labelSpanCls}>Known risks</span><textarea rows={2} className={inputCls} value={form.known_risks} onChange={e=>upd('known_risks', e.target.value)} /></label>
          <label className={`md:col-span-2 ${labelCls}`}><span className={labelSpanCls}>Market notes (optional)</span><textarea rows={2} className={inputCls} value={form.market_notes} onChange={e=>upd('market_notes', e.target.value)} /></label>
          <label className={labelCls}><span className={labelSpanCls}>Report language</span>
            <select className={inputCls} value={form.lang} onChange={e=>upd('lang', e.target.value)} data-testid="generate-lang">
              <option value="en">English</option>
              <option value="es">Español</option>
            </select>
          </label>
        </div>

        {err && <div className="text-red-400 mono text-xs">{err}</div>}

        {/* Enrichment: FEMA */}
        <div className="border hairline bg-surface1 p-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="mono text-[10px] uppercase tracking-widest2 text-gold">§ Enrichment · FEMA Flood</div>
              <div className="text-sm text-white/70 mt-2 max-w-xl">Auto-fetch FEMA National Flood Hazard Layer for this address. Adds real flood zone + BFE + FIRM panel to the report (source confidence 92).</div>
            </div>
            <div className="flex items-center gap-3">
              <label className="mono text-[10px] uppercase tracking-widest2 text-white/60 flex items-center gap-2">
                <input type="checkbox" checked={enrichFema} onChange={e=>setEnrichFema(e.target.checked)} data-testid="generate-enrich-fema" />
                Enable
              </label>
              <button type="button" onClick={checkFlood} disabled={!form.address || floodLoading} className="mono text-[10px] uppercase tracking-widest2 border hairline px-3 py-1.5 hover:border-gold hover:text-gold disabled:opacity-40" style={{transitionProperty: 'color, border-color'}} data-testid="generate-flood-preview">
                {floodLoading ? 'Checking…' : 'Preview'}
              </button>
            </div>
          </div>
          {floodPreview && !floodPreview.error && (
            <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-3 border-t hairline pt-4 mono text-xs">
              <div><div className="text-white/40 text-[10px] uppercase tracking-widest2">Zone</div><div className="text-gold text-lg mt-1">{floodPreview.zone}</div></div>
              <div><div className="text-white/40 text-[10px] uppercase tracking-widest2">Subtype</div><div className="text-white mt-1">{floodPreview.zone_subtype || '—'}</div></div>
              <div><div className="text-white/40 text-[10px] uppercase tracking-widest2">BFE</div><div className="text-white mt-1">{floodPreview.bfe != null ? `${floodPreview.bfe} ft` : '—'}</div></div>
              <div><div className="text-white/40 text-[10px] uppercase tracking-widest2">FIRM Panel</div><div className="text-white mt-1">{floodPreview.firm_panel || '—'}</div></div>
              <div>
                <div className="text-white/40 text-[10px] uppercase tracking-widest2">Risk</div>
                <div className={`mt-1 inline-block px-2 py-0.5 border ${floodPreview.risk_level === 'high' ? 'text-red-400 border-red-400/40' : floodPreview.risk_level === 'moderate' ? 'text-amber-400 border-amber-400/40' : 'text-emerald-400 border-emerald-400/40'}`}>{floodPreview.risk_level}</div>
              </div>
              <div className="col-span-2 md:col-span-5 mono text-[10px] uppercase tracking-widest2 text-white/40">
                fema_nfhl_public · confidence {floodPreview.confidence} · geocoded {(floodPreview.geocoded || {}).lat?.toFixed?.(4)}, {(floodPreview.geocoded || {}).lon?.toFixed?.(4)}
              </div>
            </div>
          )}
          {floodPreview?.error && <div className="mt-3 mono text-xs text-red-400">FEMA: {floodPreview.error}</div>}
        </div>

        {/* MLS Packet Upload */}
        <div className="border hairline bg-surface1 p-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="mono text-[10px] uppercase tracking-widest2 text-gold">§ Enrichment · MLS Packet</div>
              <div className="text-sm text-white/70 mt-2 max-w-xl">Upload a PDF or photo of the MLS listing. Gemini vision extracts asking price, DOM, sqft, year built, agent, description — injected into the report with high confidence.</div>
            </div>
            <div className="flex items-center gap-3">
              <input type="file" accept=".pdf,image/jpeg,image/png,image/webp" onChange={e => setMlsFile(e.target.files?.[0] || null)} className="mono text-xs text-white/70" data-testid="generate-mls-file" />
              <button type="button" onClick={uploadMls} disabled={!mlsFile || mlsUploading} className="mono text-[10px] uppercase tracking-widest2 border border-gold text-gold px-3 py-1.5 hover:bg-gold hover:text-obsidian disabled:opacity-40" style={{transitionProperty: 'color, background-color'}} data-testid="generate-mls-upload">
                {mlsUploading ? 'Extracting…' : 'Extract'}
              </button>
            </div>
          </div>
          {mlsError && <div className="mt-3 mono text-xs text-red-400">MLS: {mlsError}</div>}
          {mlsExtraction && (
            <div className="mt-4 border-t hairline pt-4">
              <div className="mono text-[10px] uppercase tracking-widest2 text-emerald-400 mb-3">✓ Extracted · will be included in report</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2 mono text-xs">
                {Object.entries(mlsExtraction.fields || {}).filter(([k,v]) => v !== null && v !== '' && !Array.isArray(v)).slice(0, 12).map(([k,v]) => (
                  <div key={k}>
                    <div className="text-white/40 text-[10px] uppercase tracking-widest2">{k.replace(/_/g,' ')}</div>
                    <div className="text-white mt-0.5 truncate">{typeof v === 'object' ? JSON.stringify(v) : String(v)}</div>
                  </div>
                ))}
              </div>
              {mlsExtraction.fields?.notable_features?.length > 0 && (
                <div className="mt-3 mono text-[10px] uppercase tracking-widest2 text-white/40">Features: <span className="text-white/80">{mlsExtraction.fields.notable_features.join(' · ')}</span></div>
              )}
            </div>
          )}
        </div>

        {/* Pythia Atlas Decision Pack (supplemental verified public context) */}
        <div className="border hairline bg-surface1 p-6" data-testid="atlas-panel">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="mono text-[10px] uppercase tracking-widest2 text-gold">§ Enrichment · Pythia Atlas Decision Pack</div>
              <div className="text-sm text-white/70 mt-2 max-w-xl">Live indicator matrix pulled from <span className="text-gold">pythia-atlas.onrender.com</span>. Public discovery is always attempted; protected risk / permits / insurance / score indicators require <span className="mono text-gold">PYTHIA_ATLAS_TOKEN</span>. This pack is supplemental verified public context — not the sole source of truth for the report.</div>
            </div>
            <div className="flex flex-col items-end gap-2">
              <label className="mono text-[10px] uppercase tracking-widest2 text-white/60 flex items-center gap-2 cursor-pointer" title="Walk /openapi.json for additional readonly endpoints — DENYLIST strict (blocks /admin, /ops, /ingest, /autonomy, mutating POSTs, DELETE/PATCH/PUT).">
                <input type="checkbox" checked={atlasAutoDiscover} onChange={e=>setAtlasAutoDiscover(e.target.checked)} data-testid="atlas-auto-discover-toggle" />
                Auto-discover <span className="text-gold">(readonly + denylist)</span>
              </label>
              <button type="button" onClick={fetchAtlasPack} disabled={atlasLoading || (!form.address && !form.zip_code)} className="mono text-[10px] uppercase tracking-widest2 border border-gold text-gold px-3 py-1.5 hover:bg-gold hover:text-obsidian disabled:opacity-40" style={{transitionProperty: 'color, background-color'}} data-testid="atlas-fetch-btn">
                {atlasLoading ? 'Querying Atlas…' : 'Fetch Decision Pack'}
              </button>
            </div>
          </div>
          {atlasError && <div className="mt-3 mono text-xs text-red-400" data-testid="atlas-error">Atlas: {atlasError}</div>}
          {atlasPack && (
            <div className="mt-5 border-t hairline pt-4" data-testid="atlas-pack-result">
              {/* Mode + counts banner */}
              <div className="flex flex-wrap items-center gap-3 mono text-[10px] uppercase tracking-widest2">
                <span data-testid="atlas-mode" className={`px-2 py-1 border ${atlasPack.mode === 'authenticated_full' ? 'text-emerald-400 border-emerald-400/40' : atlasPack.mode === 'authenticated_partial' ? 'text-sky-400 border-sky-400/40' : atlasPack.mode === 'live_public_only' ? 'text-amber-400 border-amber-400/40' : 'text-red-400 border-red-400/40'}`}>
                  mode: {atlasPack.mode}
                </span>
                <span className="text-white/60">auth: <span className={atlasPack.atlas?.auth_mode === 'token_configured' ? 'text-emerald-400' : 'text-amber-400'}>{atlasPack.atlas?.auth_mode}</span></span>
                <span className="text-white/60">health: <span className={atlasPack.atlas?.health_ok ? 'text-emerald-400' : 'text-red-400'}>{atlasPack.atlas?.health_ok ? 'ok' : 'fail'}</span></span>
                <span className="text-white/60">openapi: <span className={atlasPack.atlas?.openapi_ok ? 'text-emerald-400' : 'text-red-400'}>{atlasPack.atlas?.openapi_ok ? `${atlasPack.atlas?.openapi_title || 'ok'} v${atlasPack.atlas?.openapi_version || '—'}` : 'fail'}</span></span>
                {atlasPack.atlas?.auto_discover && <span className="px-2 py-0.5 border border-gold/60 text-gold">auto_discover:ON</span>}
                <span className="text-white/60">consulted: <span className="text-white">{atlasPack.counts?.total}</span> = <span className="text-white/80">{atlasPack.counts?.curated}</span> curated + <span className="text-white/80">{atlasPack.counts?.auto_discovered}</span> discovered</span>
                <span className="text-white/60">status: <span className="text-emerald-400">{atlasPack.counts?.available}✓</span> / <span className="text-amber-400">{atlasPack.counts?.auth_missing} auth_missing</span> / <span className="text-red-400">{atlasPack.counts?.unavailable} unavail</span> / <span className="text-white/50">{atlasPack.counts?.missing_input} miss_input</span></span>
              </div>
              {atlasPack.mode === 'live_public_only' && (
                <div className="mt-3 border border-amber-400/40 bg-amber-400/5 p-3 mono text-[11px] text-amber-200" data-testid="atlas-public-only-warning">
                  <span className="text-amber-400 uppercase tracking-widest2">⚠ live_public_only</span> — No <span className="mono">PYTHIA_ATLAS_TOKEN</span> configured. Protected indicators (risk, permits, insurance, score, gentrification) will return <span className="mono">auth_missing</span>. Add the token in Emergent Secrets to unlock the full pack.
                </div>
              )}
              {atlasPack.mode === 'authenticated_partial' && (
                <div className="mt-3 border border-sky-400/40 bg-sky-400/5 p-3 mono text-[11px] text-sky-200">
                  <span className="text-sky-400 uppercase tracking-widest2">◐ authenticated_partial</span> — Token configured, but only some protected indicators returned data. Check the reasons column below.
                </div>
              )}
              {atlasPack.mode === 'authenticated_full' && (
                <div className="mt-3 border border-emerald-400/40 bg-emerald-400/5 p-3 mono text-[11px] text-emerald-200">
                  <span className="text-emerald-400 uppercase tracking-widest2">● authenticated_full</span> — All attempted protected indicators returned live data.
                </div>
              )}
              {atlasPack.mode === 'atlas_unreachable' && (
                <div className="mt-3 border border-red-400/40 bg-red-400/5 p-3 mono text-[11px] text-red-200">
                  <span className="text-red-400 uppercase tracking-widest2">✕ atlas_unreachable</span> — Atlas /health did not respond. The pack is best-effort only.
                </div>
              )}

              {/* Indicator matrix */}
              <div className="mt-4 overflow-x-auto">
                <table className="w-full mono text-[11px]">
                  <thead>
                    <tr className="text-white/40 uppercase tracking-widest2 text-[10px] border-b hairline">
                      <th className="text-left px-2 py-2">ID</th>
                      <th className="text-left px-2 py-2">Endpoint</th>
                      <th className="text-left px-2 py-2">Method</th>
                      <th className="text-left px-2 py-2">Protected</th>
                      <th className="text-left px-2 py-2">Status</th>
                      <th className="text-left px-2 py-2">Value / Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(atlasPack.indicators || []).map(ind => {
                      const statusColor =
                        ind.status === 'available' ? 'text-emerald-400 border-emerald-400/40' :
                        ind.status === 'auth_missing' ? 'text-amber-400 border-amber-400/40' :
                        ind.status === 'auth_failed' ? 'text-red-400 border-red-400/40' :
                        ind.status === 'missing_input' ? 'text-white/50 border-white/20' :
                        ind.status === 'unsupported_by_adapter' ? 'text-white/50 border-white/20' :
                        'text-red-400 border-red-400/40';
                      const summary = ind.status === 'available'
                        ? (() => {
                            const d = ind.data;
                            if (!d) return '—';
                            if (typeof d === 'string') return d.slice(0, 80);
                            if (Array.isArray(d)) return `array[${d.length}]`;
                            const entries = Object.entries(d).slice(0, 3).map(([k,v]) => `${k}=${typeof v === 'object' ? JSON.stringify(v).slice(0,30) : String(v).slice(0,40)}`);
                            return entries.join(' · ');
                          })()
                        : (ind.reason || '—');
                      return (
                        <tr key={ind.id} className="border-b hairline/50 hover:bg-surface2/40" data-testid={`atlas-ind-${ind.id}`}>
                          <td className="px-2 py-1.5 text-gold whitespace-nowrap">{ind.id}</td>
                          <td className="px-2 py-1.5 text-white/70 whitespace-nowrap">{ind.source_endpoint}</td>
                          <td className="px-2 py-1.5 text-white/50">{ind.method}</td>
                          <td className="px-2 py-1.5 text-white/50">{ind.protected ? 'yes' : 'no'}</td>
                          <td className="px-2 py-1.5"><span className={`inline-block px-1.5 py-0.5 border ${statusColor} uppercase tracking-widest2 text-[9px]`}>{ind.status}</span></td>
                          <td className="px-2 py-1.5 text-white/80 truncate max-w-md" title={typeof summary === 'string' ? summary : JSON.stringify(summary)}>{summary}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="mt-3 mono text-[10px] uppercase tracking-widest2 text-white/40">
                pack_id={atlasPack._id || '—'} · adapter v{atlasPack.adapter_version} · generated {atlasPack.generated_at ? new Date(atlasPack.generated_at).toLocaleString() : '—'}
              </div>
              <div className="mt-2 mono text-[10px] text-white/50">
                ✓ This pack will be injected into the Claude prompt as <span className="text-gold">&lt;PYTHIA_ATLAS_DECISION_PACK&gt;</span> supplemental verified public context. Claude may cite Atlas only for indicators marked <span className="text-emerald-400">available</span>.
              </div>

              {/* Auto-discovery denylist trace */}
              {atlasPack.atlas?.auto_discover && (atlasPack.discovered_endpoints || []).length > 0 && (
                <details className="mt-4 border hairline bg-obsidian/50 p-3" data-testid="atlas-discovery-trace">
                  <summary className="mono text-[10px] uppercase tracking-widest2 text-gold cursor-pointer">
                    ▸ Auto-discovery trace ({atlasPack.discovered_endpoints.length} paths considered, {atlasPack.discovered_endpoints.filter(e=>!e.allowed).length} blocked by denylist)
                  </summary>
                  <div className="mt-3 max-h-64 overflow-y-auto">
                    <table className="w-full mono text-[10px]">
                      <thead className="text-white/40 uppercase tracking-widest2">
                        <tr className="border-b hairline">
                          <th className="text-left p-1">Path</th>
                          <th className="text-left p-1">Method</th>
                          <th className="text-left p-1">Source</th>
                          <th className="text-left p-1">Status</th>
                          <th className="text-left p-1">Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {atlasPack.discovered_endpoints.map((e, i) => (
                          <tr key={i} className="border-b hairline/50">
                            <td className="p-1 text-white/80">{e.endpoint}</td>
                            <td className="p-1 text-white/60">{e.method}</td>
                            <td className="p-1 text-white/50">{e.source}</td>
                            <td className={`p-1 ${e.allowed ? 'text-emerald-400' : 'text-red-400'}`}>{e.allowed ? 'allowed' : 'blocked'}</td>
                            <td className="p-1 text-white/50">{e.block_reason || e.reason || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </details>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end">
          <button type="submit" disabled={status === 'sending'} className="inline-flex items-center gap-3 px-8 py-3.5 bg-gold text-obsidian mono text-[11px] uppercase tracking-widest2 hover:bg-white disabled:opacity-50" style={{transitionProperty: 'background-color'}} data-testid="generate-submit">
            {status === 'sending' ? 'Generating… (~60–90s with Claude Sonnet 4.5)' : 'Generate with Pythia'} <span>→</span>
          </button>
        </div>
      </form>
    </div>
  );
}

// ============ REPORTS ============
function ReportsList() {
  const [reports, setReports] = useState([]);
  useEffect(() => { api.get('/admin/reports', { headers: authHeaders() }).then(r => setReports(r.data)); }, []);
  return (
    <div>
      <h1 className="serif text-4xl">Reports</h1>
      <div className="mt-8 border hairline bg-surface1">
        <div className="grid grid-cols-12 mono text-[10px] uppercase tracking-widest2 text-white/40 border-b hairline">
          <div className="col-span-4 p-3">Address</div>
          <div className="col-span-2 p-3">Verdict</div>
          <div className="col-span-1 p-3 text-right">Conf.</div>
          <div className="col-span-2 p-3 text-right">Asking</div>
          <div className="col-span-2 p-3">Issued</div>
          <div className="col-span-1 p-3 text-right">Open</div>
        </div>
        {reports.map(r => (
          <div key={r.id} className="grid grid-cols-12 border-b hairline last:border-0 mono text-xs hover:bg-surface2/40">
            <div className="col-span-4 p-3 text-white">{r.address}</div>
            <div className="col-span-2 p-3">
              <span className={`verdict-badge verdict-${r.report?.verdict}`}>{r.report?.verdict?.toUpperCase()}</span>
            </div>
            <div className="col-span-1 p-3 text-right text-white/70">{r.report?.verdict_confidence}</div>
            <div className="col-span-2 p-3 text-right text-white/70">{r.asking_price ? `$${Number(r.asking_price).toLocaleString()}` : '—'}</div>
            <div className="col-span-2 p-3 text-white/60">{new Date(r.created_at).toLocaleDateString()}</div>
            <div className="col-span-1 p-3 text-right">
              <Link to={`/report/${r.id}`} className="text-gold hover:text-white" data-testid={`report-open-${r.id}`}>Open →</Link>
            </div>
          </div>
        ))}
        {reports.length === 0 && <div className="p-8 text-center mono text-xs text-white/40">No reports yet. Generate your first.</div>}
      </div>
    </div>
  );
}

// ============ RADAR ============
function Radar() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get('/admin/territories', { headers: authHeaders() }).then(r => setRows(r.data)); }, []);
  return (
    <div>
      <h1 className="serif text-4xl">Territory Radar</h1>
      <p className="mt-2 text-white/60">Signal-scored territories. Retainer clients see this weekly.</p>
      <div className="mt-8 border hairline bg-surface1">
        <div className="grid grid-cols-12 mono text-[10px] uppercase tracking-widest2 text-white/40 border-b hairline">
          <div className="col-span-1 p-3">ZIP</div>
          <div className="col-span-3 p-3">Territory</div>
          <div className="col-span-2 p-3">Signal</div>
          <div className="col-span-1 p-3 text-right">Absentee</div>
          <div className="col-span-1 p-3 text-right">Stale</div>
          <div className="col-span-1 p-3 text-right">Permits</div>
          <div className="col-span-1 p-3 text-right">DOM</div>
          <div className="col-span-1 p-3 text-right">Δ%</div>
          <div className="col-span-1 p-3">Flood</div>
        </div>
        {rows.map(r => (
          <div key={r.id} className="grid grid-cols-12 border-b hairline last:border-0 mono text-xs hover:bg-surface2/40">
            <div className="col-span-1 p-3 text-gold">{r.zip_code}</div>
            <div className="col-span-3 p-3 text-white">{r.name}</div>
            <div className="col-span-2 p-3 flex items-center gap-2"><span className="w-16 signal-bar"><span style={{width: `${r.signal_score}%`}}/></span><span className="text-white/70">{r.signal_score}</span></div>
            <div className="col-span-1 p-3 text-right text-white/70">{r.absentee_owners}</div>
            <div className="col-span-1 p-3 text-right text-white/70">{r.stale_listings}</div>
            <div className="col-span-1 p-3 text-right text-white/70">{r.permits_last_90d}</div>
            <div className="col-span-1 p-3 text-right text-white/70">{r.median_dom}</div>
            <div className={`col-span-1 p-3 text-right ${r.price_change_pct < 0 ? 'text-red-400' : 'text-emerald-400'}`}>{r.price_change_pct > 0 ? '+' : ''}{r.price_change_pct}%</div>
            <div className="col-span-1 p-3"><span className={`inline-block px-1.5 py-0.5 text-[10px] uppercase ${r.flood_risk === 'high' ? 'text-red-400 border border-red-400/40' : r.flood_risk === 'moderate' ? 'text-amber-400 border border-amber-400/40' : 'text-emerald-400 border border-emerald-400/40'}`}>{r.flood_risk}</span></div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============ PILOT ============
function Pilot() {
  const [entries, setEntries] = useState([]);
  const [form, setForm] = useState({ client: '', type: 'deal_report', price: 100, property_address: '', delivery_hours: 48 });
  const load = () => api.get('/admin/pilot', { headers: authHeaders() }).then(r => setEntries(r.data));
  useEffect(() => { load(); }, []);
  const add = async (e) => {
    e.preventDefault();
    await api.post('/admin/pilot', { ...form, price: Number(form.price), delivery_hours: Number(form.delivery_hours) }, { headers: authHeaders() });
    setForm({ client: '', type: 'deal_report', price: 100, property_address: '', delivery_hours: 48 });
    load();
  };
  const toggle = async (id, field, value) => {
    await api.patch(`/admin/pilot/${id}`, { [field]: value }, { headers: authHeaders() });
    load();
  };
  const remove = async (id) => {
    await api.delete(`/admin/pilot/${id}`, { headers: authHeaders() });
    load();
  };

  const inputCls = "bg-obsidian border hairline px-3 py-2 mono text-sm text-white placeholder-white/30 focus:border-gold outline-none";

  return (
    <div>
      <h1 className="serif text-4xl">Pilot Tracker</h1>
      <p className="mt-2 text-white/60">The commercial ledger. Every deal, every dollar, every referral.</p>

      <form onSubmit={add} className="mt-8 grid grid-cols-1 md:grid-cols-6 gap-2 border hairline bg-surface1 p-4" data-testid="pilot-add-form">
        <input required placeholder="Client" className={inputCls} value={form.client} onChange={e=>setForm({...form, client:e.target.value})} data-testid="pilot-client" />
        <select className={inputCls} value={form.type} onChange={e=>setForm({...form, type:e.target.value})}>
          <option value="deal_report">Deal Report</option>
          <option value="territory">Territory</option>
          <option value="intelligence_desk">Intel Desk</option>
        </select>
        <input required type="number" placeholder="Price" className={inputCls} value={form.price} onChange={e=>setForm({...form, price:e.target.value})} data-testid="pilot-price" />
        <input placeholder="Property" className={`${inputCls} md:col-span-2`} value={form.property_address} onChange={e=>setForm({...form, property_address:e.target.value})} data-testid="pilot-property" />
        <button type="submit" className="bg-gold text-obsidian mono text-[10px] uppercase tracking-widest2 hover:bg-white px-3 py-2" style={{transitionProperty: 'background-color'}} data-testid="pilot-add-submit">+ Add</button>
      </form>

      <div className="mt-6 border hairline bg-surface1">
        <div className="grid grid-cols-12 mono text-[10px] uppercase tracking-widest2 text-white/40 border-b hairline">
          <div className="col-span-2 p-3">Client</div>
          <div className="col-span-2 p-3">Property</div>
          <div className="col-span-1 p-3">Type</div>
          <div className="col-span-1 p-3 text-right">Price</div>
          <div className="col-span-1 p-3">Decision</div>
          <div className="col-span-1 p-3 text-center">Paid</div>
          <div className="col-span-1 p-3 text-center">Repeat</div>
          <div className="col-span-1 p-3 text-center">Referral</div>
          <div className="col-span-2 p-3">Feedback</div>
        </div>
        {entries.map(e => (
          <div key={e.id} className="grid grid-cols-12 border-b hairline last:border-0 mono text-xs hover:bg-surface2/40" data-testid={`pilot-row-${e.id}`}>
            <div className="col-span-2 p-3 text-white">{e.client}</div>
            <div className="col-span-2 p-3 text-white/70">{e.property_address || '—'}</div>
            <div className="col-span-1 p-3 text-white/60">{e.type}</div>
            <div className="col-span-1 p-3 text-right text-gold">${Number(e.price).toLocaleString()}</div>
            <div className="col-span-1 p-3">
              <select value={e.decision || ''} onChange={ev => toggle(e.id, 'decision', ev.target.value)} className="bg-transparent text-white/80 border hairline px-1 py-0.5 text-[10px]" data-testid={`pilot-decision-${e.id}`}>
                <option value="">—</option>
                <option value="proceed">proceed</option>
                <option value="negotiate">negotiate</option>
                <option value="hold">hold</option>
                <option value="reject">reject</option>
              </select>
            </div>
            <div className="col-span-1 p-3 text-center"><input type="checkbox" checked={!!e.paid} onChange={ev => toggle(e.id, 'paid', ev.target.checked)} data-testid={`pilot-paid-${e.id}`} /></div>
            <div className="col-span-1 p-3 text-center"><input type="checkbox" checked={!!e.repeat} onChange={ev => toggle(e.id, 'repeat', ev.target.checked)} /></div>
            <div className="col-span-1 p-3 text-center"><input type="checkbox" checked={!!e.referral} onChange={ev => toggle(e.id, 'referral', ev.target.checked)} /></div>
            <div className="col-span-2 p-3 flex items-center gap-2">
              <input defaultValue={e.feedback || ''} onBlur={ev => toggle(e.id, 'feedback', ev.target.value)} className="flex-1 bg-transparent text-white/70 border hairline px-2 py-1 text-[10px]" placeholder="feedback" />
              <button onClick={() => remove(e.id)} className="text-red-400 hover:text-red-300 text-[10px]">✕</button>
            </div>
          </div>
        ))}
        {entries.length === 0 && <div className="p-8 text-center mono text-xs text-white/40">No pilot entries yet.</div>}
      </div>
    </div>
  );
}

// ============ LEADS ============
function Leads() {
  const [leads, setLeads] = useState([]);
  useEffect(() => { api.get('/admin/leads', { headers: authHeaders() }).then(r => setLeads(r.data)); }, []);
  return (
    <div>
      <h1 className="serif text-4xl">Leads</h1>
      <div className="mt-8 border hairline bg-surface1">
        <div className="grid grid-cols-12 mono text-[10px] uppercase tracking-widest2 text-white/40 border-b hairline">
          <div className="col-span-3 p-3">Name</div>
          <div className="col-span-3 p-3">Email</div>
          <div className="col-span-2 p-3">Role</div>
          <div className="col-span-2 p-3">Source</div>
          <div className="col-span-2 p-3">Received</div>
        </div>
        {leads.map(l => (
          <div key={l.id} className="grid grid-cols-12 border-b hairline last:border-0 mono text-xs hover:bg-surface2/40">
            <div className="col-span-3 p-3 text-white">{l.name}</div>
            <div className="col-span-3 p-3 text-white/70">{l.email}</div>
            <div className="col-span-2 p-3 text-white/60">{l.role}</div>
            <div className="col-span-2 p-3 text-white/60">{l.source}</div>
            <div className="col-span-2 p-3 text-white/50">{new Date(l.created_at).toLocaleString()}</div>
          </div>
        ))}
        {leads.length === 0 && <div className="p-8 text-center mono text-xs text-white/40">No leads yet.</div>}
      </div>
    </div>
  );
}

// ============ ROOT ============
export default function Admin() {
  const [authed, setAuthed] = useState(!!getAdminToken());

  useEffect(() => {
    if (!getAdminToken()) return;
    // Verify token still valid
    api.post('/admin/verify', {}, { headers: authHeaders() }).catch(() => {
      setAdminToken(null);
      setAuthed(false);
    });
  }, []);

  if (!authed) {
    return (
      <div>
        <Nav />
        <AdminLogin onLogin={() => setAuthed(true)} />
        <Footer />
      </div>
    );
  }

  const logout = () => { setAdminToken(null); setAuthed(false); };

  return (
    <div>
      <Nav />
      <AdminShell onLogout={logout}>
        <Routes>
          <Route index element={<Overview />} />
          <Route path="intakes" element={<Intakes />} />
          <Route path="generate" element={<Generate />} />
          <Route path="reports" element={<ReportsList />} />
          <Route path="radar" element={<Radar />} />
          <Route path="pilot" element={<Pilot />} />
          <Route path="leads" element={<Leads />} />
        </Routes>
      </AdminShell>
      <DisclaimerBar />
      <Footer />
    </div>
  );
}
